import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useInterval } from 'usehooks-ts';

import { createdBy } from '~components/Job/AuditDetails/utils';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { canManageCompanyOnboarding } from '~hooks/useCompanyOnboarding/companyOnboardingAccess';
import useCompanyOnboarding from '~hooks/useCompanyOnboarding/useCompanyOnboarding';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCompanyOnboardingProgressQuery from '~queries/useCompanyOnboardingProgressQuery';
import { companyOnboardingStepPath } from '~router/routePaths';
import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  CaptionText,
  CompletionToggleButton,
  completionToggleButtonProps,
  OnboardingLink,
  ProgressQueryStatus,
  SecondaryText,
  TitleText,
} from './CompanyOnboardingPrimitives';

const POLL_INTERVAL_MS = 60_000;

const LAST_UPDATED_TICK_MS = 60_000;

const LastUpdatedLabel = ({ dataUpdatedAt }: { dataUpdatedAt: number }) => {
  const [now, setNow] = useState(() => dayjs());

  useInterval(() => setNow(dayjs()), dataUpdatedAt ? LAST_UPDATED_TICK_MS : null);

  return dataUpdatedAt ? (
    <CaptionText sx={{ mt: 1 }}>
      {t('company_onboarding.last_updated', {
        duration: dayjs(dataUpdatedAt).from(now, true),
      })}
    </CaptionText>
  ) : null;
};

const CompanyOnboardingChecklist = observer(() => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const canManageOnboarding = canManageCompanyOnboarding(userStore);
  const {
    completedCount,
    completedStepsByKey,
    dataUpdatedAt,
    hasLoaded,
    isError,
    isPending,
    percentComplete,
    stepKeys,
    totalCount,
  } = useCompanyOnboardingProgressQuery({
    companyId,
    refetchIntervalMs: POLL_INTERVAL_MS,
  });
  const { toggleStep } = useCompanyOnboarding();

  return (
    <Box sx={{ maxWidth: '800px' }}>
      <SettingsPageHeader
        title={t('company_onboarding.title')}
        description={t('company_onboarding.subtitle')}
      />
      <ProgressQueryStatus isError={isError} isPending={isPending} />
      {hasLoaded ? (
        <>
          <TitleText>
            {t('company_onboarding.progress', {
              completed: completedCount,
              total: totalCount,
            })}
          </TitleText>
          <LinearProgress value={percentComplete} variant="determinate" sx={{ mt: 1 }} />
          <LastUpdatedLabel dataUpdatedAt={dataUpdatedAt} />
          <SecondaryText sx={{ mt: 2 }}>
            {t('company_onboarding.denominator_note')}
          </SecondaryText>
          {canManageOnboarding ? null : (
            <SecondaryText sx={{ mt: 1 }}>
              {t('company_onboarding.edit_company_required')}
            </SecondaryText>
          )}
          <Box sx={{ mt: 2 }}>
            {stepKeys.map((stepKey) => {
              const completedStep = completedStepsByKey.get(stepKey);

              return (
                <Box
                  data-test-id={`company-onboarding-module-${stepKey}`}
                  key={stepKey}
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    gap: 2,
                    py: 2,
                  })}
                >
                  <CompletionToggleButton
                    disabled={!canManageOnboarding}
                    isCompleted={Boolean(completedStep)}
                    isIconOnly
                    onToggle={() => toggleStep(companyId, stepKey)}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <OnboardingLink to={companyOnboardingStepPath(stepKey)} variant="h6">
                      {t(`company_onboarding.modules.${stepKey}.title`)}
                    </OnboardingLink>
                    <SecondaryText sx={{ mt: 0.5 }}>
                      {t(`company_onboarding.modules.${stepKey}.body`)}
                    </SecondaryText>
                    <SecondaryText sx={{ mt: 0.5 }}>
                      {t('company_onboarding.done_means', {
                        outcome: t(`company_onboarding.modules.${stepKey}.done_means`),
                      })}
                    </SecondaryText>
                    {completedStep?.completedBy ? (
                      <CaptionText sx={{ mt: 0.5 }}>
                        {t('company_onboarding.completed_attribution', {
                          completedAt: dateFormat(completedStep.completedAt),
                          name: createdBy(completedStep.completedBy),
                          // The global escapeValue renders the date's slashes as &#x2F;.
                          interpolation: { escapeValue: false },
                        })}
                      </CaptionText>
                    ) : null}
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <SmallButton
                      {...completionToggleButtonProps(Boolean(completedStep))}
                      component={RouterLink}
                      to={companyOnboardingStepPath(stepKey)}
                    >
                      {completedStep
                        ? t('company_onboarding.actions.review')
                        : t('company_onboarding.actions.set_up')}
                    </SmallButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      ) : null}
    </Box>
  );
});

export default CompanyOnboardingChecklist;
